/**
 * Route guard for the 3D lab pages.
 * three.js (r163+) only runs on WebGL 2. Without it, R3F's <Canvas> fails silently
 * and the lab loader spins forever, so explain the problem up front instead.
 */

import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MonitorX, RefreshCw, ArrowLeft } from 'lucide-react';

function hasWebGL2() {
    let gl: WebGL2RenderingContext | null = null;
    try { gl = document.createElement('canvas').getContext('webgl2'); } catch { /* privacy add-ons may throw */ }
    const ok = !!gl && !gl.isContextLost(); // a lost context also makes three.js fail
    gl?.getExtension('WEBGL_lose_context')?.loseContext(); // free the probe context
    return ok;
}

export default function RequireWebGL() {
    const navigate = useNavigate();
    const location = useLocation();
    const [ok] = useState(hasWebGL2); // once per entry into the lab routes

    if (ok) return <Outlet />;

    return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-4">
            <div className="max-w-xl w-full bg-card border border-border rounded-2xl shadow-2xl p-8">
                <div className="w-16 h-16 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-5">
                    <MonitorX className="w-8 h-8 text-amber-500" />
                </div>
                <h1 className="text-2xl font-bold text-center mb-2">3D graphics are turned off</h1>
                <p className="text-muted-foreground text-center mb-6">
                    The virtual lab needs <strong className="text-foreground">WebGL 2</strong>, which this browser has disabled or can't use on this device.
                </p>

                <div className="space-y-3 text-sm">
                    <div className="rounded-lg bg-muted/50 border border-border p-3">
                        <p className="font-semibold mb-1">Firefox / LibreWolf</p>
                        <p className="text-muted-foreground">
                            Settings → General → Performance: untick "Use recommended performance settings", tick
                            "Use hardware acceleration when available", then restart the browser. Still blocked? Open
                            <code className="mx-1 px-1 rounded bg-muted">about:config</code> and set
                            <code className="mx-1 px-1 rounded bg-muted">webgl.disabled</code> to false,
                            <code className="mx-1 px-1 rounded bg-muted">webgl.enable-webgl2</code> and
                            <code className="mx-1 px-1 rounded bg-muted">webgl.force-enabled</code> to true.
                        </p>
                    </div>
                    <div className="rounded-lg bg-muted/50 border border-border p-3">
                        <p className="font-semibold mb-1">Chrome / Edge</p>
                        <p className="text-muted-foreground">
                            Settings → System: turn on "Use graphics acceleration when available", then relaunch.
                        </p>
                    </div>
                    <div className="rounded-lg bg-muted/50 border border-border p-3">
                        <p className="font-semibold mb-1">Safari / iPad</p>
                        <p className="text-muted-foreground">Update to iOS / Safari 15 or newer.</p>
                    </div>
                    <p className="text-muted-foreground text-center">
                        Check your browser at{' '}
                        <a href="https://get.webgl.org/webgl2/" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">
                            get.webgl.org/webgl2
                        </a>
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                    <button
                        onClick={() => window.location.reload()}
                        className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Try Again
                    </button>
                    <button
                        onClick={() => (location.key === 'default' ? navigate('/') : navigate(-1))}
                        className="px-5 py-2.5 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/70 transition-colors flex items-center justify-center gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Go Back
                    </button>
                </div>
            </div>
        </div>
    );
}
